import { useMemo } from "react";
import { Handover } from "graphql/generated/graphql";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { formatDate } from "utils/helpers";
import { HandoverStatus, OrganizationType } from "constants/field-values";

interface HandoverTimelineProps {
	// Accepts the full handover object when available (list/detail views)
	handover?: Pick<
		Handover,
		| "status"
		| "vendor_organization_id"
		| "created_at"
		| "updated_at"
		| "published_at"
		| "requester"
		| "customer_organization_id"
	> | null;
}

const getStepImg = (step: number, isCompleted: boolean, isPrevCompleted: boolean): string => {
	if (isCompleted) return "completed-circle";
	if (isPrevCompleted) return `${step}-circle-purple`;
	return `${step}-circle`;
};

const HandoverTimeline = ({ handover }: HandoverTimelineProps) => {
	const { isUserAnon: isAnon, loggedUserValue } = useLoggedUser();

	const { isVendor, isCustomerPath } = useMemo(() => {
		let isVendor = !isAnon;
		let isCustomerPath = !isAnon;

		if (handover) {
			// Determine role: compare logged user's org against the handover's vendor org.
			isVendor = loggedUserValue.organization_id === handover.vendor_organization_id;

			const requesterOrgId = handover?.requester?.organization_id ?? "";
			isCustomerPath = requesterOrgId === handover?.customer_organization_id;
		} else {
			// Force a role perspective when no handover object is available
			isVendor = loggedUserValue.organization?.type === OrganizationType.Vendor;
			isCustomerPath = !isVendor;
		}

		return { isVendor, isCustomerPath };
	}, [isAnon, handover, loggedUserValue.organization_id, loggedUserValue.organization]);

	// Resolve values from the handover object or individual prop overrides
	const status = handover?.status;
	const createdAt = handover?.created_at;
	const updatedAt = handover?.updated_at;
	const publishedAt = handover?.published_at;

	// Step 2: vendor has submitted — customer review is pending or done
	const isStep2Completed =
		status === HandoverStatus.PENDING_CUSTOMER ||
		status === HandoverStatus.PUBLISHED ||
		status === HandoverStatus.CANCELLED;

	// Step 3: customer approved and published
	const isStep3Completed = status === HandoverStatus.PUBLISHED;

	const step1Img = !!handover ? "completed-circle" : "1-circle-purple"; // Always complete once the handover exists
	const step1Date = formatDate(createdAt);
	const step1Label = useMemo(() => {
		if (!handover) {
			return "Started";
		}

		if (isCustomerPath) {
			return "Requested";
		} else {
			return "Started";
		}
	}, [handover, isCustomerPath]);

	const step2Img = useMemo(() => {
		switch (status) {
			case HandoverStatus.VENDOR_DRAFT:
				return `2-circle`;
			case HandoverStatus.PENDING_VENDOR:
				return `2-circle-purple`;
			case HandoverStatus.PENDING_CUSTOMER:
			case HandoverStatus.PUBLISHED:
				return "completed-circle";
			default:
				return `2-circle`;
		}
	}, [status, isVendor, isCustomerPath]);
	const step2Date = isStep2Completed ? formatDate(updatedAt) : undefined;
	const step2Label = useMemo(() => {
		switch (status) {
			case HandoverStatus.PENDING_VENDOR:
				return "Pending vendor completion";
			case HandoverStatus.VENDOR_DRAFT:
				if (isVendor) {
					return "Customer review";
				} else {
					return "Pending vendor completion";
				}
			case HandoverStatus.PENDING_CUSTOMER:
				if (isVendor) {
					return "Pending customer review";
				} else {
					return "Submitted by vendor";
				}
			default:
				if (isVendor) {
					return "Customer review";
				} else {
					return "Vendor completion";
				}
		}
	}, [status, isVendor]);

	const step3Img = getStepImg(3, isStep3Completed, isStep2Completed);
	const step3Date = isStep3Completed ? formatDate(publishedAt) : undefined;
	const step3Label = useMemo(() => {
		if (status === HandoverStatus.PENDING_CUSTOMER && !isVendor) {
			return "Approve & publish";
		}

		return "Approved and published";
	}, [status, isVendor]);

	return (
		<div className="ht-timeline">
			{/* Step 1 — Requested / Created */}
			<div>
				<img src={`${process.env.PUBLIC_URL}/assets/images/icons/step-${step1Img}.svg`} alt="Step 1" />
				<div className="ht-timeline-description">
					<div>{step1Label}</div>
					{step1Date && <div>{step1Date}</div>}
				</div>
			</div>

			<div className="ht-hr" />

			{/* Step 2 — Vendor submission */}
			<div>
				<img src={`${process.env.PUBLIC_URL}/assets/images/icons/step-${step2Img}.svg`} alt="Step 2" />
				<div className="ht-timeline-description">
					<div>{step2Label}</div>
					{isStep2Completed && step2Date && <div>{step2Date}</div>}
				</div>
			</div>

			<div className="ht-hr" />

			{/* Step 3 — Customer approval */}
			<div>
				<img src={`${process.env.PUBLIC_URL}/assets/images/icons/step-${step3Img}.svg`} alt="Step 3" />
				<div className="ht-timeline-description">
					<div>{step3Label}</div>
					{step3Date && <div>{step3Date}</div>}
				</div>
			</div>
		</div>
	);
};

export default HandoverTimeline;
